import { IcAlert, IcBT, IcTro } from '@assets/icons/40x40';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { useAppSelector } from '@hooks/redux.hooks';
import { useCabinetCommand } from '@hooks/useCabinetCommand';
import { selectSensorConfigurationById } from '@redux/situation/monitoring.selectors';
import { getCabinetSectionStatuses, isSectionRepulsionOn } from '@utils/sensors/sensors.utils';

import { AutomatonSensorConfiguration, SpecificStateEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

export default function CabinetCommands({
  configuration,
  status,
}: Readonly<SensorOrSubSensorInfoByType<SubSensorTypeEnum.AUTOMATON_CABINET>>) {
  const { formatMessage } = appIntl();

  const sectionStatuses = getCabinetSectionStatuses(status);

  const parentAutomatonConfiguration = useAppSelector((state) =>
    selectSensorConfigurationById(state, false, configuration.sensorId),
  ) as AutomatonSensorConfiguration | null;

  const useCabinet = useCabinetCommand({
    parentSensorConfiguration: parentAutomatonConfiguration,
    cabinetConfiguration: configuration,
  });

  if (!parentAutomatonConfiguration || !useCabinet.canCommand) {
    return null;
  }

  const countDetection = sectionStatuses.filter((sectionStatus) => !isSectionRepulsionOn(sectionStatus)).length;

  const alarmCount = status
    ? status.subSensorStatuses.reduce((acc, curr) => {
        const lowerStatus = curr.lowerSegment?.specificState === SpecificStateEnum.EFFRACTION ? 1 : 0;
        const upperStatus = curr.upperSegment?.specificState === SpecificStateEnum.EFFRACTION ? 1 : 0;
        return acc + lowerStatus + upperStatus;
      }, 0)
    : 0;

  const sleepActions = [
    {
      action: 'on',
      i18nKey: 'sensors.tab.commands.cabinet.sleep.on',
    },
    {
      action: 'off',
      i18nKey: 'sensors.tab.commands.cabinet.sleep.off',
    },
  ];

  const defenceActions = [
    {
      action: 'detection',
      i18nKey: 'sensors.tab.commands.cabinet.defence.detection',
      disabled: !useCabinet.canDetect,
    },
    {
      action: 'repulsion',
      i18nKey: 'sensors.tab.commands.cabinet.defence.repulsion',
      disabled: !useCabinet.canRepulse,
    },
  ];

  const alartmActions = [
    {
      action: 'detection',
      i18nKey: 'sensors.tab.commands.cabinet.alarm.reinitialize',
      values: { count: alarmCount },
      disabled: !useCabinet.canDetect,
    },
  ];

  const canRepulseOrDetect = useCabinet.canRepulse || useCabinet.canDetect;

  return (
    <>
      <DataSheetCard
        icon={IcBT}
        description={formatMessage({ id: 'sensors.tab.commands.cabinet.sleep.description' })}
        actions={sleepActions}
        isDisabled={status === null}
        isLoading={useCabinet.isSleepCommandLoading}
        onClick={(action) => useCabinet.sleep(action !== 'on')}
      />
      {canRepulseOrDetect && (
        <DataSheetCard
          icon={IcTro}
          description={formatMessage({ id: 'sensors.tab.commands.cabinet.defence.description' })}
          subDescription={formatMessage(
            { id: 'sensors.tab.commands.cabinet.defence.counter' },
            { countDetection, total: sectionStatuses.length },
          )}
          actions={defenceActions}
          isDisabled={status === null}
          isLoading={useCabinet.isRepulsionCommandLoading}
          onClick={(action) => useCabinet.repulse(action !== 'detection')}
        />
      )}
      <DataSheetCard
        icon={IcAlert}
        description={formatMessage({ id: 'sensors.tab.commands.cabinet.alarm.description' })}
        subDescription={formatMessage({ id: 'sensors.tab.commands.cabinet.alarm.counter' }, { count: alarmCount })}
        actions={alartmActions}
        isDisabled={status === null || alarmCount === 0}
        isLoading={useCabinet.isReinitializationCommandLoading}
        onClick={useCabinet.reinitialize}
      />
    </>
  );
}
